import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setSearch, setCountryData, setSearchResults } from "../../store/search/searchSlice";
import { ReactComponent as Spinner } from "../../assets/icons/spinner.svg";
import { ReactComponent as GptSearch } from "../../assets/promptIcons/gptSearch.svg";
import { ReactComponent as AI_Icons } from "../../assets/promptIcons/ai-icons.svg";
import { ReactComponent as ArrowLeft } from "../../assets/icons/arrowleft.svg";
import { ReactComponent as Earth } from "../../assets/icons/earth.svg";
import { ReactComponent as Magnifier } from "../../assets/icons/Magnifier.svg";
import { ReactComponent as GPS } from "../../assets/icons/GPS.svg";
import { Countries } from "../public/Home/CountryFlag";
import { clearSignatoryAddress } from "../../store/Company/companySlice";
import Icon from "./Home/v2/Icon";

import {
  SEARCH_LENGTH_ERROR,
  SEARCH_COMPANY_NOT_FOUND,
  SEARCH_BUTTON_TITLE,
} from "../../constants/constants";
import useCountryCode from "../../Hooks/useCountryCode";

// GCC markets shown first in the hero country dropdown (matches the handoff).
const GCC_CODES = ["AE", "SA", "QA", "KW", "BH", "OM"];

// ISO 3166-1 alpha-2 code -> regional-indicator flag emoji. The homepage
// handoff sources flags as inline emoji (e.g. "🇦🇪"), not images, so we derive
// them from each country's code rather than the unreliable Wikimedia file_url.
const codeToFlagEmoji = (code) => {
  if (!code || !/^[A-Za-z]{2}$/.test(code)) return "🌐";
  const cc = code.toUpperCase();
  return String.fromCodePoint(...[...cc].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
};

/* `resultsPath` is where a submitted search lands. */
const Search = ({ prompt, variant, resultsPath = "/search", setShowPrompt = () => { }, showPrompt, setSelectedPrompt = () => { } }) => {
  const [loading, setLoading] = useState(false);
  const { countryCode } = useCountryCode();
  const [error, setSearchError] = useState(false);
  const searchInputRef = useRef();
  const [country, setCountry] = useState({
    name: "Select Country",
    flag: "",
    code: "",
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [displayUl, setDisplayUl] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const location = useLocation();

  const dropdownRef = useRef(null);

  const handleCountrySelect = (countryCode) => {
    const countryData = Countries.find(country => country.code === countryCode);
    const countryDetail = {
      name: countryData.name,
      flag: countryData.file_url,
      code: countryData.code,
    };
    dispatch(setCountryData(countryDetail));
    setCountry(countryDetail)
  }

  useEffect(() => {
    dispatch(clearSignatoryAddress());
    localStorage.removeItem("industryData");
    localStorage.removeItem("financialData");
    localStorage.removeItem("companyData");
    localStorage.removeItem("uploadData");
    localStorage.removeItem("companyAddress");
    localStorage.removeItem("authorizedSignatory");
    localStorage.removeItem("userSelectedCompany");
    localStorage.removeItem("createCompany");
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDisplayUl(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const isHome = location.pathname === "/";
  const isSearch = location.pathname.includes("search");
  const { searchQuery, searchCount, countryData } = useSelector(
    (state) => state.search
  );

  useEffect(() => {
    if (isHome) {
      setCountry(
        countryData
      );
    } else {
      searchInputRef.current.value = searchQuery;
      setCountry(countryData);
    }
  }, [searchQuery, isHome, countryData]);

  // Hero variant defaults to the UAE (matches the design) so the first search
  // works without forcing a manual country pick.
  useEffect(() => {
    if (variant === "hero" && !countryData) {
      const ae = Countries.find((c) => c.code === "AE");
      if (ae) {
        const detail = { name: ae.name, flag: ae.file_url, code: ae.code };
        dispatch(setCountryData(detail));
        setCountry(detail);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  const enterClick = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch(e);
    }
  };

  const handleCountry = (e, flag, option) => {
    const countryDetail = {
      name: option.name,
      flag: option.file_url,
      code: option.code,
    };
    dispatch(setCountryData(countryDetail));
    setCountry(countryDetail);
    setDisplayUl(!displayUl);
  };

  const handleDisplayUl = (e) => {
    e.preventDefault();
    setDisplayUl(!displayUl);
  };
  const handleSearch = (e) => {
    e.preventDefault();
    if (!countryData && !prompt) {
      setDisplayUl(true);
      return;
    } else {
      setDisplayUl(false);
    }
    if (searchInputRef.current.value.length < 3) {
      setSearchError(true);
      return;
    }
    setLoading(true);
    !showPrompt && dispatch(setSearchResults([]));
    setShowPrompt(false);
    setSelectedPrompt({});
    dispatch(setSearch(searchInputRef.current.value));
    dispatch(setCountryData(country));
    navigate(prompt ? "/gpt-prompts/search" : resultsPath);
    setSearchError(false);

    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };
  const handleFilteredData = (event) => {
    const searchLetters = event.target.value;
    setSearchTerm(searchLetters);

    const filteredData = Countries.filter((item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(filteredData);
  };

  const handleLocationPopup = async (e) => {
    e.preventDefault();
    try {
      handleCountrySelect(countryCode)
    } catch (error) {
    }
    setDisplayUl(false);
  };

  // Hero variant — the redesigned home page search bar (TIP-518).
  // Reuses every handler/state above; only the presentation differs so
  // the search behaviour stays identical to the default variant.
  if (variant === "hero") {
    const q = (searchTerm || "").trim().toLowerCase();
    const matchesQuery = (c) =>
      !q || c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q);
    const gccCountries = GCC_CODES
      .map((code) => Countries.find((c) => c.code === code))
      .filter(Boolean)
      .filter(matchesQuery);
    const otherCountries = Countries.filter(
      (c) => !GCC_CODES.includes(c.code)
    ).filter(matchesQuery);

    const renderCountryRow = (option) => (
      <button
        key={option.code}
        type="button"
        className="hero-country-row"
        onClick={(e) => handleCountry(e, option.file_url, option)}
      >
        <span className="hero-country-flag-emoji">{codeToFlagEmoji(option.code)}</span>
        <span className="hero-country-row-name">{option.name}</span>
        <span className="hero-country-code">{option.code}</span>
        {country?.code === option.code && (
          <Icon name="check" size={14} color="var(--coral)" />
        )}
      </button>
    );

    return (
      <div ref={dropdownRef} className="hero-searchwrap">
        <div className="hero-search">
          <button type="button" className="hero-country" onClick={handleDisplayUl} aria-label="Select country">
            <span className="hero-country-flag-emoji">{codeToFlagEmoji(country?.code)}</span>
            <span className="hero-country-name">{country?.name || "Select Country"}</span>
            <Icon name="chevron-down" size={14} />
          </button>
          <div className="hero-search-divider" />
          <input
            type="search"
            id="hero-search-input"
            className="hero-search-input"
            placeholder="Search by company name"
            aria-label="Search a business"
            required
            ref={searchInputRef}
            onKeyDown={enterClick}
          />
          <button type="button" className="btn btn-primary hero-search-btn" onClick={handleSearch}>
            {loading ? <Spinner /> : (<>Search <Icon name="arrow-right" size={16} /></>)}
          </button>
        </div>

        {error && <div className="hero-search-error">{SEARCH_LENGTH_ERROR}</div>}

        {displayUl && (
          <div className="card fade-in hero-country-menu">
            <div className="hero-country-search">
              <Magnifier />
              <input
                type="text"
                placeholder="Search country"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button type="button" className="hero-country-gps" onClick={handleLocationPopup}>
              <GPS /> Locate Using GPS
            </button>
            <div className="hero-country-list">
              {gccCountries.length > 0 && (
                <>
                  <div className="hero-country-group">GCC · Primary market</div>
                  {gccCountries.map(renderCountryRow)}
                </>
              )}
              {otherCountries.length > 0 && (
                <>
                  <div className="hero-country-group">Other countries</div>
                  {otherCountries.map(renderCountryRow)}
                </>
              )}
              {gccCountries.length === 0 && otherCountries.length === 0 && (
                <div className="hero-country-empty">No country matches “{searchTerm}”</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {/* <form> */}
        <div ref={dropdownRef} className={`${(isHome || prompt) && "max-w-6xl"} mx-auto`}>
          <div
            style={{
              backgroundColor: (prompt) ? "#202123" : "rgba(221, 221, 221, 0.6)"
            }}
            className={`relative flex flex-col bg-homeSearch sm:p-3 p-2  ${(prompt) ? "rounded-[12px]" : "rounded-lg md:rounded-full"}`}
          >
            <div className="relative w-full flex flex-col md:flex-row gap-1 items-center">
              <div className={`sm:flex block text-sm text-gray-900 border ${prompt ? "w-full border-gradient inputBorder" : "flex align-center w-full border-none rounded-full focus:border-none sm:px-4 bg-white md:w-4/5 max-h-[56px] py-2"}`}>
                {!prompt && <div
                  className="w-[90px] md:w-2/5 flex justify-between items-center sm:text-black  border-r p-4 cursor-pointer truncate bg-white rounded-l-full"
                  onClick={handleDisplayUl}
                >
                  <div
                    className="cursor-pointer flex text-left text-xs overflow-hidden"
                    role="presentation"
                  >
                    {country?.flag ? (
                      <img
                        src={country.flag}
                        width="20px"
                        height="20px"
                        className="mr-2 h-5"
                        alt="hello"
                      />
                    ) : <Earth className="md:hidden mr-2" />}

                    {<span className="hidden md:block">{country?.name || "Select Country"}</span>}
                  </div>
                  <ArrowLeft className={displayUl ? "rotate-90" : "-rotate-90"} />
                </div>}
                <input
                  type="search"
                  id="search"
                  className={`${prompt ? "w-full rounded-[8px] bg-[#353541] text-white pr-20 w-full" : "rounded-full  w-[80%] md:w-full bg-white"} p-3 h-10 sm:h-auto`}
                  placeholder={prompt ? "Search here..." : "Search a business by name"}
                  required
                  ref={searchInputRef}
                  onKeyDown={enterClick}
                />
              </div>
              {/* <button onClick={handleSearch} type="button" className={prompt ? */}
              <button onClick={handleSearch} type="button" className={prompt ? "z-[2] absolute right-10 top-1/2 -translate-y-1/2" : "flex items-center justify-center text-white md:max-w-[220px] sm:mt-0 mt-2 w-full md:w-1/5 bg-primary text-sm rounded-lg sm:rounded-full px-3 py-2 h-[56px] md:block shadow-[0_-2px_0_#bc3214] transform transition-all duration-300 hover:shadow-[0_0_0_#0000] hover:-translate-y-[2px] font-semibold"}>
              {loading ? <span className="flex justify-center items-center"><Spinner /></span> : prompt ? <GptSearch /> : SEARCH_BUTTON_TITLE}
                  </button>
            </div>

            {prompt && <div className="w-full h-full flex justify-center items-center sm:pt-3 pt-2">
              {/* <p className="text-[#EBF0F3] text-[12px] sm:text-[15px] text-bold">Unleash the true Power of AI...</p> */}
              <AI_Icons />
            </div>}

            <div
              className={`${displayUl ? "" : "hidden"
                } shadow-boxPopup rounded-md mt-2 sm:w-96 w-80 bg-white p-5 sm:max-h-84 sm:min-h-fit absolute top-[44px] right-[9px] md:top-[66px] md:left-[30px] z-10`}
            >
              <div
                className="text-link border-b border-slate p-3 mb-3 font-semibold cursor-pointer items-center flex"
                onClick={handleLocationPopup}
              >
                <GPS className="mr-3" /> Locate Using GPS
              </div>
              <div className="flex items-center">
                <Magnifier className="absolute mb-3 mx-3" />

                <input
                  type="text"
                  className="w-full border-input border w-full p-2 px-11 mb-3 shadow-search rounded"
                  onKeyUp={handleFilteredData}
                />
              </div>
              <ul className="overflow-y-auto shadow-boxPopup border-greyFade border max-h-36 min-h-36 sm:max-h-64 sm:min-h-fit rounded-md">
                {filteredData.map((option) => (
                  <li
                    onClick={(e) =>
                      handleCountry(e.target.innerText, option.file_url, option)
                    }
                    className="px-4 py-3 cursor-pointer hover:bg-input flex text-left text-xs"
                    role="presentation"
                  >
                    <img
                      src={option.file_url}
                      width="20px"
                      height="20px"
                      className="mr-2 h-5"
                      alt="hello"
                    />
                    {option.name}
                  </li>
                ))}
              </ul>
            </div>
          </div>

        </div>
        {error && (
          <div className="max-w-4xl mx-auto text-left pt-2 pl-2 text-red-400 text-[10px]">
            {prompt ? "Prompt should have a minimum length of three characters." : SEARCH_LENGTH_ERROR}
          </div>
        )}
        {/* {searchCount > 150 && ( <div className="text-center pt-4 text-red-600"> {SEARCH_COMPANY_NOT_FOUND} </div> )} */}
      {/* </form> */}
    </>
  );
};

export default Search;
